import * as Sentry from '@sentry/react';
import { FC, useMemo, useRef, useState } from 'react';

import {
  useCurrentEntertainerQuery,
  useFetchCurrentEntertainer,
} from '@queries/EntertainerQueries';

import { useAddEntertainerMediaMutation } from '@mutations/EntertainerMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useUploadFile } from '@hooks/useUploadFile';

import Button from '@components/buttons/Button';
import { Input } from '@components/form/Input';
import { VideoUploadField } from '@components/form/VideoUploadField';
import Card from '@components/layout/Card/Card';
import { Popup } from '@components/layout/Popup/Popup';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';

const readFile = (file: Blob): Promise<string | ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => {
      Sentry.captureException(error);
      reject(error);
    };
  });
};

const AddVideoContent: FC<{
  message?: string;
  onSave: () => void;
  cityId: string;
  meta?: { channel?: string; channelAction?: string };
}> = ({ onSave, message, cityId, meta: initialMeta = {} }) => {
  const { track } = useAnalytics();
  const fetchCurrentEntertainer = useFetchCurrentEntertainer();
  const { entertainer } = useCurrentEntertainerQuery();
  const { createAuthGateHandler } = useAuthGate();
  const [title, setTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState<File>();
  const [meta, setMeta] = useState<Record<string, string>>(initialMeta);
  const [error, setError] = useState<string>();
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const { isSubmitting, addEntertainerMediaAsync } = useAddEntertainerMediaMutation();
  const { uploadProgress, startUpload } = useUploadFile();
  const didSubmitRef = useRef(false);

  const handleSubmit = () => {
    track('Video Submitted', { title });
    setError(undefined);

    createAuthGateHandler(
      async () => {
        try {
          if (!didSubmitRef.current && selectedFile) {
            didSubmitRef.current = true;
            let scopedEntertainer = entertainer;

            if (!scopedEntertainer) {
              const { entertainer: updatedEntertainer } = await fetchCurrentEntertainer();
              scopedEntertainer = updatedEntertainer;
            }

            if (scopedEntertainer) {
              setIsUploadingVideo(true);
              let mediaId: string;
              try {
                ({ mediaId } = await startUpload(selectedFile));
              } finally {
                setIsUploadingVideo(false);
              }

              await addEntertainerMediaAsync({
                entertainerId: scopedEntertainer._id,
                title,
                type: 'video',
                meta,
                data: undefined,
                mediaId,
              });
            }
            onSave?.();
            didSubmitRef.current = false;
          }
        } catch (error) {
          Sentry.captureException(error, { extra: { title } });
          setError('Something went wrong uploading your video. Please try again.');
          didSubmitRef.current = false;
        }
      },
      {
        entertainerCallback: () => {},
        cityId,
      },
    );
  };

  const buttonText = useMemo(() => {
    if (isUploadingVideo) {
      return uploadProgress.percentage > 0
        ? `Uploading... (${uploadProgress.percentage.toFixed(2)}%)`
        : 'Uploading...';
    }

    if (isSubmitting) {
      return 'Saving...';
    }

    return 'Upload';
  }, [uploadProgress, isSubmitting, isUploadingVideo]);

  return (
    <div className="d-grid gap-2">
      <Card>
        <VideoUploadField
          name="featuredVideo"
          placeholder="Select a video"
          onChange={(file) => setSelectedFile(file)}
        />
      </Card>
      <Input
        placeholder={`Title (e.g. "Gagggy Reveal") *`}
        name="featuredVideoTitle"
        onChange={(event) => setTitle(event.target.value)}
      />
      <Input
        placeholder="Video creds"
        name="featuredVideoCreds"
        onChange={(event) =>
          setMeta((prevValue) => ({ ...prevValue, videoCreds: event.target.value }))
        }
      />
      {error && <p className="text-danger mt-1 mb-0">{error}</p>}
      <div>
        <Button
          roundness="rounded"
          disabled={!selectedFile || !title || isSubmitting || isUploadingVideo}
          onClick={handleSubmit}
        >
          {buttonText}
        </Button>
      </div>
    </div>
  );
};

export const AddFeaturedVideoPopup: FC<{
  isVisible: boolean;
  onClose: () => void;
  cityId: string;
  message?: string;
  meta?: {
    channel?: string;
    channelAction?: string;
  };
}> = ({ isVisible, onClose, message, cityId, meta }) => {
  return (
    <Popup isVisible={isVisible} height="full" onClose={onClose}>
      <Popup.Content>
        <TitleToolbar className="mt-2" text="Add Featured Video" size="md" />
        {message && <p className="mt-0 mb-2">{message}</p>}
        {isVisible && <AddVideoContent onSave={onClose} meta={meta} cityId={cityId} />}
      </Popup.Content>
    </Popup>
  );
};
